import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { colors, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/material";
import { Login } from "@mui/icons-material";
import { isProductionMode } from "../../utils/helper";
import CornerRibbon from "../../components/CornerRibbon";
import workz360 from "../../images/workz360.png";
import { CardBase } from "../../components/global";

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: "12px 24px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#12903C",
  color: "#ffffff",
  boxShadow: "0 4px 14px rgba(18, 144, 60, 0.4)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#0f7a32",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(18, 144, 60, 0.6)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 20px",
    fontSize: "0.875rem",
  },
}));

const SignupCompleted = () => {
  const navigate = useNavigate();
  useTitle("Signup Completed");
  const isProduction = React.useMemo(() => isProductionMode(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { xs: "100%", sm: "100vh" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
        backgroundImage: "url('/background-home.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", sm: "fixed" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 480 },
          position: "relative",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {!isProduction && (
            <CornerRibbon
              position="top-right"
              backgroundColor={colors.yellow[200]}
              fontColor="#333"
            >
              STAGING
            </CornerRibbon>
          )}
          <CardBase
            useGlassCard={true}
            nested={false}
            sx={{
              p: { xs: 2.5, sm: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <Box
                component="img"
                src={workz360}
                alt="Workz360"
                sx={{
                  height: { xs: 44, sm: 64 },
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))",
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  letterSpacing: 0.5,
                  textAlign: "center",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              >
                Thank You For Registering!
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, 
                  textAlign: "center",
                  px: { xs: 1, sm: 0 },
                }}
              >
                Your account has been successfully created. You can now log in to access your account.
              </Typography>
            </Box>
            <StyledButton
              variant="contained"
              startIcon={<Login />}
              sx={{ mt: { xs: 1.5, sm: 2 } }}
              onClick={() => navigate("/login")}
            >
              Back to Login
            </StyledButton>
          </CardBase>
        </Box>
      </Box>
    </Box>
  );
};
export default SignupCompleted;
